import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { SEO, seoConfig } from "@/hooks/useSEO";
import { generateWebPageSchema, generateBreadcrumbSchema, generateArticleSchema } from "@/lib/jsonld";

// Images
import coverImage from "@/assets/blog/programmes-fidelite/couverture.webp";
import saturationImage from "@/assets/blog/programmes-fidelite/saturation.webp";
import classiqueVsWalletImage from "@/assets/blog/programmes-fidelite/classique-vs-wallet.webp";
import diagnostiquerImage from "@/assets/blog/programmes-fidelite/diagnostiquer.webp";
import erreursImage from "@/assets/blog/programmes-fidelite/erreurs-identifiees.webp";

const ProgrammesFideliteEchouent = () => {
  const jsonLdSchemas = [
    generateWebPageSchema(
      "Pourquoi 58 % des programmes de fidélité sont abandonnés",
      seoConfig.blogProgrammesFidelite.description,
      "/programme-fidelite-ne-fonctionne-pas"
    ),
    generateBreadcrumbSchema([
      { name: "Accueil", path: "/" },
      { name: "Blog", path: "/blog" },
      { name: "Programme de fidélité ne fonctionne pas", path: "/programme-fidelite-ne-fonctionne-pas" }
    ]),
    generateArticleSchema(
      "Pourquoi 58 % des programmes de fidélité sont abandonnés (et comment éviter les mêmes erreurs)",
      seoConfig.blogProgrammesFidelite.description,
      "https://unotif.com/programme-fidelite-ne-fonctionne-pas",
      "2026-03-18"
    )
  ];

  const imageStyle = {
    boxShadow: "0 8px 32px -8px rgba(0,0,0,0.12), 0 4px 16px -4px rgba(0,0,0,0.08)",
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO {...seoConfig.blogProgrammesFidelite} jsonLd={jsonLdSchemas} />
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="pt-24 lg:pt-40 pb-12 sm:pb-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Breadcrumb */}
            <nav className="text-sm font-medium text-[#495456] mb-6">
              <Link to="/blog" className="text-[#0c97a9] hover:underline">Blog</Link>
              <span className="mx-2">›</span>
              <span className="text-[#074155]">Programme de fidélité ne fonctionne pas</span>
            </nav>

            {/* H1 */}
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] mb-8">
              Pourquoi 58 % des programmes de fidélité sont abandonnés (et comment éviter les mêmes erreurs)
            </h1>

            {/* Cover Image */}
            <img
              src={coverImage}
              alt="Programme de fidélité ne fonctionne pas"
              loading="eager"
              className="rounded-2xl w-full h-auto"
              style={imageStyle}
            />
          </div>
        </section>

        {/* Article Content */}
        <section className="pb-20 sm:pb-28 lg:pb-36">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">

            {/* Introduction */}
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">58 % des membres d'un programme de fidélité ne l'utilisent plus après 6 mois.</strong> Ce n'est pas une question de budget. Ce n'est pas une question de secteur. C'est une question de conception.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La plupart des commerçants qui lancent un <strong className="font-semibold">programme de fidélité</strong> font les mêmes erreurs. Ils créent quelque chose qui a du sens pour eux, mais qui ne tient pas sa promesse côté client. Les membres s'inscrivent, utilisent le programme une ou deux fois, puis passent à autre chose.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Cet article décortique les 5 erreurs qui tuent un programme de fidélité, avec pour chacune un correctif précis et actionnable. Si vous cherchez d'abord une vue d'ensemble des outils disponibles, notre guide sur les <Link to="/meilleurs-outils-de-fidelisation-client" className="text-[#0c97a9] hover:underline font-semibold">outils de fidélisation client</Link> couvre le terrain avant d'entrer dans les détails.
            </p>

            <hr className="my-10 border-gray-200" />

            {/* H2 - Pourquoi votre programme ne fonctionne pas */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Pourquoi votre programmes de fidélité ne fonctionne pas en 2026 ?
            </h2>

            {/* Image Saturation */}
            <img
              src={saturationImage}
              alt="Saturation des programmes de fidélité"
              loading="lazy"
              className="rounded-2xl w-full h-auto my-8"
              style={imageStyle}
            />

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un consommateur est membre, en moyenne, de <strong className="font-semibold">14 programmes de fidélité</strong>. Il n'en utilise activement que 6 à 7. Les autres dorment dans son application, sur sa carte ou quelque part dans sa boîte mail.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Cette saturation est réelle, mais elle n'explique pas tout. La vraie raison de l'échec est plus fondamentale : <strong className="font-semibold">la plupart des programmes sont conçus du point de vue de l'entreprise, pas du client</strong>.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un programme conçu pour l'entreprise accumule des données, génère des visites à court terme, et semble rentable sur le papier. Un programme conçu pour le client crée un sentiment de reconnaissance, une relation durable, une raison de revenir qui dépasse le bon d'achat. La différence entre les deux tient à 5 erreurs bien précises.
            </p>

            {/* CTA encart info - désactivé temporairement (double ancre)
            <div className="my-8 p-6 rounded-2xl bg-[#f0fafb] border border-[#0c97a9]/20">
              <p className="text-[14px] sm:text-base font-semibold text-[#074155]">
                👉 <Link to="/meilleurs-outils-de-fidelisation-client" className="text-[#0c97a9] hover:underline">Découvrez quels outils de fidélisation fonctionnent réellement</Link>
              </p>
            </div>
            */}


            <hr className="my-10 border-gray-200" />

            {/* Erreur n°1 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Erreur n°1 : des récompenses hors de portée
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">54 % des clients abandonnent un programme de fidélité quand la récompense leur semble inatteignable.</strong> C'est l'erreur de calibration la plus fréquente, et la plus dévastatrice.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Prenons un cas concret : un salon de coiffure propose 1 point par euro dépensé, et 200 points pour obtenir une coupe offerte. La coupe vaut 30 euros. Il faut donc dépenser 200 euros, soit 6 à 7 visites au tarif moyen, avant de toucher la première récompense. À mi-parcours, le client a oublié qu'il participait.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La psychologie est claire sur ce point. Ce qui motive à continuer n'est pas la valeur de la récompense finale : c'est la <strong className="font-semibold">perception du progrès vers cette récompense</strong>. Un programme bien calibré rend la première récompense accessible en <strong className="font-semibold">3 à 5 visites maximum</strong>. Le client ressent qu'il avance, et cette progression l'incite à revenir.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le correctif est direct : réduisez les seuils ou augmentez la valeur perçue de chaque visite. Attribuer 10 points par visite plutôt que 1 point par euro change radicalement la perception, même si la mécanique de fond reste identique.
            </p>

            <hr className="my-10 border-gray-200" />

            {/* Erreur n°2 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Erreur n°2 : une inscription qui demande trop d'efforts
            </h2>

            {/* Image Programme classique vs wallet */}
            <img
              src={classiqueVsWalletImage}
              alt="Programme classique vs wallet"
              loading="lazy"
              className="rounded-2xl w-full h-auto my-8"
              style={imageStyle}
            />

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les clients sont disposés à rejoindre un <strong className="font-semibold">programme de fidélité</strong>. Ils ne sont pas disposés à y passer cinq minutes.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Formulaire à remplir, application à télécharger, compte à créer, mot de passe à mémoriser, carte physique à ne pas perdre : chaque étape supplémentaire réduit le taux d'inscription. Les données le confirment : <strong className="font-semibold">les clients sont 3 fois moins enclins à rejoindre un programme si l'adhésion dépasse 2 minutes</strong>.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La carte plastique ajoute un frein quotidien. Combien de fois vos clients ont-ils dit "j'ai pas ma carte sur moi" ? À chaque fois, la visite ne compte pas, et l'engagement baisse d'un cran.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le correctif : <strong className="font-semibold">l'adhésion doit tenir en 30 secondes</strong>. Un QR code affiché en caisse, scanné depuis le téléphone déjà en poche, et la carte s'ajoute automatiquement dans le wallet du client. Pas de téléchargement, pas de compte, pas de mot de passe.
            </p>

            <hr className="my-10 border-gray-200" />

            {/* Erreur n°3 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Erreur n°3 : le silence qui suit l'inscription
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">85 % des clients découvrent un programme de fidélité via un vendeur en caisse.</strong> Et dans la grande majorité des cas, c'est aussi la dernière fois qu'ils en entendent parler.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le scénario se répète partout : le client s'inscrit, reçoit une confirmation, puis plus rien pendant des semaines. Sans rappel, sans stimulus, sans raison de repenser à la carte, l'engagement s'évapore. Au bout de 3 mois sans contact, le programme n'existe plus dans l'esprit du client.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un <strong className="font-semibold">programme de fidélisation</strong> fonctionne comme une relation : il se nourrit d'un contact régulier. Pas forcément fréquent. Pas forcément commercial. Mais régulier.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le correctif minimal : <strong className="font-semibold">1 communication par mois</strong> à votre base de membres. Un rappel de points en attente, une offre flash réservée aux membres, l'annonce d'un événement. Le canal fait une vraie différence : une notification push via wallet arrive directement sur l'écran du téléphone, sans dépendre d'un algorithme ni d'un filtre anti-spam.
            </p>

            <hr className="my-10 border-gray-200" />

            {/* Erreur n°4 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Erreur n°4 : traiter tous les clients de façon identique
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Offrir exactement la même chose à un client qui vient une fois par an et à un client qui vient trois fois par semaine, c'est envoyer un message clair : personne n'est vraiment reconnu ici.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les <strong className="font-semibold">clients à haute valeur</strong> le perçoivent. Sans traitement différencié, leur engagement reste superficiel. Ils conservent leur carte parce que c'est gratuit, pas parce qu'ils se sentent valorisés. Et quand un concurrent leur propose une expérience plus personnalisée, la transition devient facile.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La <strong className="font-semibold">segmentation client</strong> n'exige pas une infrastructure complexe. Deux catégories suffisent pour commencer : client occasionnel (moins de 2 visites par trimestre) et client régulier (3 visites ou plus). Les messages, les offres et les seuils de récompense varient d'un groupe à l'autre. Les plateformes de <strong className="font-semibold">wallet marketing</strong> actuelles permettent cette segmentation sans compétences techniques.
            </p>

            <hr className="my-10 border-gray-200" />

            {/* Erreur n°5 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Erreur n°5 : piloter sans données
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La plupart des commerçants ne savent pas combien de clients ont utilisé une récompense le mois dernier. Ni quel pourcentage de membres est encore actif après 90 jours. Sans ces chiffres, il est impossible de distinguer un programme qui fonctionne d'un programme qui tourne dans le vide.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un <strong className="font-semibold">programme de fidélité</strong> sans indicateurs de suivi est une dépense, pas un investissement. Trois métriques suffisent pour avoir une vision opérationnelle :
            </p>
            <ul className="list-disc pl-6 space-y-3 mb-6">
              <li className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
                Le <strong className="font-semibold">taux de rédemption</strong> : le pourcentage de récompenses effectivement utilisées sur celles distribuées. En dessous de 20 %, les récompenses ne sont pas assez attractives ou restent trop peu connues des membres.
              </li>
              <li className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
                Le <strong className="font-semibold">taux de rétention</strong> : le pourcentage de membres encore actifs après 90 jours. En dessous de 50 %, le programme perd ses membres trop vite pour créer un effet de fidélisation durable.
              </li>
              <li className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
                La <strong className="font-semibold">fréquence de visite</strong> : le nombre moyen de passages par client actif sur 30 jours. Si ce chiffre stagne après la mise en place du programme, l'effet attendu n'est pas au rendez-vous.
              </li>
            </ul>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Pour aller plus loin sur les indicateurs à suivre et savoir exactement comment les calculer, notre guide sur <Link to="/mesurer-fidelite-client-kpi" className="text-[#0c97a9] hover:underline font-semibold">les 6 KPI indispensables pour mesurer la fidélité client</Link> détaille chaque métrique avec des formules et des seuils de référence.
            </p>

            <hr className="my-10 border-gray-200" />

            {/* Comment diagnostiquer */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Comment diagnostiquer rapidement l'état de votre programme
            </h2>

            {/* Image Diagnostiquer */}
            <img
              src={diagnostiquerImage}
              alt="Diagnostiquer état de programme de fidélisation"
              loading="lazy"
              className="rounded-2xl w-full h-auto my-8"
              style={imageStyle}
            />

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Avant de tout repenser, passez votre programme au crible de ce tableau :
            </p>

            {/* Tableau diagnostic */}
            <div className="overflow-x-auto my-8">
              <table className="w-full border-collapse text-[13px] sm:text-sm">
                <thead>
                  <tr className="bg-[#074155] text-white">
                    <th className="px-4 py-3 text-left font-semibold rounded-tl-xl">Signal d'alerte</th>
                    <th className="px-4 py-3 text-left font-semibold">Ce que ça révèle</th>
                    <th className="px-4 py-3 text-left font-semibold rounded-tr-xl">Action prioritaire</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="px-4 py-3 font-medium text-[#495456]">Moins de 20 % des membres ont utilisé une récompense</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Seuils trop hauts ou programme méconnu</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Baisser les seuils + envoyer une relance ciblée</td>
                  </tr>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <td className="px-4 py-3 font-medium text-[#495456]">Le nombre d'inscrits stagne depuis 3 mois</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Adhésion trop complexe</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Passer au QR code wallet, supprimer les étapes inutiles</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="px-4 py-3 font-medium text-[#495456]">Aucune communication envoyée depuis 6 semaines</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Programme en veille involontaire</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Planifier au moins 1 message par mois via le canal le plus direct</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium text-[#495456]">Taux de réachat inférieur à 30 % sur la base membres</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Récompenses peu attractives ou mal communiquées</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Retravailler la proposition de valeur et tester un nouveau format</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Ce tableau est un outil de triage rapide. Il identifie en 5 minutes le blocage principal, sans analyse approfondie.
            </p>

            <hr className="my-10 border-gray-200" />

            {/* Ajuster ou repartir de zéro */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Ajuster ou repartir de zéro ?
            </h2>

            {/* Image Erreurs identifiées */}
            <img
              src={erreursImage}
              alt="Erreurs de fidélisation identifiées"
              loading="lazy"
              className="rounded-2xl w-full h-auto my-8"
              style={imageStyle}
            />

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La réponse dépend du nombre d'erreurs identifiées dans le tableau ci-dessus.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Votre programme souffre d'une ou deux erreurs isolées : un ajustement ciblé suffit. Baisser un seuil, simplifier l'inscription, planifier une séquence de communication mensuelle. Les premiers effets sont visibles en 4 à 6 semaines.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Trois erreurs ou plus sont présentes simultanément : une refonte complète produit de meilleurs résultats qu'un empilement de correctifs sur une base mal conçue. Repartir d'une mécanique simple et d'un canal d'animation direct va plus vite qu'on ne le pense.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Reconstruire un <strong className="font-semibold">programme de fidélité</strong> prend aujourd'hui moins d'une semaine avec les bons outils. Une carte wallet personnalisée, un QR code en caisse et les premières notifications push peuvent être opérationnels en 48 heures : sans développeur, sans budget conséquent.
            </p>

            <hr className="my-10 border-gray-200" />

            {/* Conclusion */}
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les 5 erreurs présentées ici couvrent la grande majorité des cas d'échec des <strong className="font-semibold">programmes de fidélisation client</strong>. Elles ont toutes un point commun : elles naissent d'un programme conçu trop vite, sans se mettre à la place du client.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Corriger une erreur à la fois reste une approche valable. Mais si votre programme cumule plusieurs de ces problèmes, le temps investi à le patcher vaut moins que le temps investi à reconstruire quelque chose de solide. Unotif vous permet de créer et d'animer une carte wallet en quelques minutes. Testez gratuitement et envoyez votre première notification push dès aujourd'hui.
            </p>

          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ProgrammesFideliteEchouent;
